import { Injectable, TemplateRef } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  BaseControl,
  TextArea,
  Textbox,
  Const,
  Custom,
  UsuarioActual
} from '@app/shared';
import { result, find, isEmpty } from 'lodash';
import { AuthenticationService } from '@app/core';

@Injectable()
export class CptFormService {
  private user: UsuarioActual;
  // Define cuando mostrar etapa del tramite
  showStageProcedure = false;
  constructor(
    private as: AuthenticationService
  ) {
    this.user = as.getCredentials();
  }

  /**
   * Retorna los controles necesarios para crear el formulario para
   * gestionar las categorias.
   *
   * @param {*} [obj]
   * @returns {BaseControl<any>[]}
   * @memberof CptFormService
   */
  getFieldsCategory(obj: any): BaseControl<any>[] {
    const controls: BaseControl<any>[] = [
      new Textbox({
        key: 'empresa_categoria',
        type: 'hidden',
        value: !isEmpty(obj.empresa_categoria) ?
                obj.empresa_categoria
               : this.user ?
                this.user.info_persona.id_empresa
               : null,
        order: 3
      })
    ];
    return controls.concat(this.getFieldsBase(obj, 'categoria')).sort((a, b) => a.order - b.order);
  }

  /**
   * Retorna los controles necesarios para crear el formulario para
   * gestionar los procesos.
   *
   * @param {*} [obj]
   * @returns {BaseControl<any>[]}
   * @memberof CptFormService
   */
  getFieldsProcess(template: TemplateRef<any>, obj: any): BaseControl<any>[] {
    const controls: BaseControl<any>[] = [];
    if (!isEmpty(obj.categories)) {
      controls.push(
        new Custom({
          key: 'proceso_categoria',
          label: 'Categoría:',
          template: template,
          placeholder: 'Categoría del proceso',
          options: obj.categories,
          value: obj.proceso_categoria,
          order: 4,
          validations: {
            validators: [Validators.required]
          }
        })
      );
    }
    return controls.concat(this.getFieldsBase(obj, 'proceso')).sort((a, b) => a.order - b.order);
  }

  /**
   * Retorna los controles necesarios para crear el formulario para
   * gestionar los trámites.
   *
   * @param {*} [obj]
   * @returns {BaseControl<any>[]}
   * @memberof CptFormService
   */
  getFieldsTramite(template: TemplateRef<any>, obj: any): BaseControl<any>[] {
    const controls: BaseControl<any>[] = [
      new Custom({
        key: 'tramite_estado',
        label: 'Etapa:',
        template: template,
        placeholder: 'Etapa del trámite',
        options: obj.defaultStatusTramite,
        value: !isEmpty(obj.tramite_estado) ? obj.tramite_estado.id : result(
          find(obj.defaultStatusTramite, {
            value: Const.STATUS_TRAMITE.active
          }), 'key'
        ),
        hidden: !this.showStageProcedure,
        disabled: !this.showStageProcedure,
        order: 4,
        validations: {
          validators: [Validators.required]
        }
      })
    ];
    if (!isEmpty(obj.processes)) {
      controls.push(
        new Custom({
          key: 'tramite_proceso',
          label: 'Proceso:',
          template: template,
          placeholder: 'Proceso del trámite',
          options: obj.processes,
          value: obj.tramite_proceso.id,
          order: 3,
          validations: {
            validators: [Validators.required]
          }
        })
      );
    }
    return controls.concat(this.getFieldsBase(obj, 'tramite')).sort((a, b) => a.order - b.order);
  }

  /**
   * Retorna los controles necesarios para crear el formulario para
   * gestionar los procesos secundarios.
   *
   * @param {*} [obj]
   * @returns {BaseControl<any>[]}
   * @memberof CptFormService
   */
  getFieldsSecondaryProcess(template: TemplateRef<any>, obj: any): BaseControl<any>[] {
    const controls: BaseControl<any>[] = [
      new Custom({
        key: 'lista_roles_secundaria',
        label: 'Grupos de la secundaria:',
        template: template,
        options: obj.defaultRoles,
        placeholder: 'Seleccione los grupos del sistema que puedan iniciar el proceso secundario',
        describe: 'Grupos del sistema que pueden iniciar la secundaria.',
        value: obj.roles ? this.getOptions(obj.roles, 'id_rol') : [],
        order: 3,
        validations: {
          validators: [Validators.required]
        },
        parameters: {multiple: true}
      }),
      new Custom({
        key: 'lista_etapas_tramite',
        label: 'Etapas del trámite:',
        template: template,
        placeholder: 'Seleccione las etapas del trámite en los cuales la secundaria puede ser activada',
        describe: 'Etapas del trámite en los cuales se puede iniciar la secundaria.',
        options: obj.defaultStatusTramite,
        hidden: !this.showStageProcedure,
        disabled: !this.showStageProcedure,
        value: obj.etapas ? this.getOptions(obj.etapas, 'id_etapa_tramite') : [],
        order: 4,
        validations: {
          validators: []
        },
        parameters: {multiple: true}
      }),
      new Custom({
        key: 'lista_estados_expediente',
        label: 'Estados del expediente:',
        template: template,
        placeholder: 'Seleccione los estados del expediente en los cuales la secundaria puede ser activada',
        describe: 'Estados del expediente en los cuales se puede iniciar la secundaria.',
        options: obj.defaultStatusExpediente,
        value: obj.etapas ? this.getOptions(obj.etapas, 'id_estado_expediente') : [],
        order: 5,
        validations: {
          validators: []
        },
        parameters: {multiple: true}
      }),
      new Custom({
        key: 'estado_expediente_inicial',
        label: 'Cambio del estado del expediente:',
        template: template,
        placeholder: 'Seleccione el estado por el cual cambiara el expediente una vez iniciada la secundaria',
        describe: 'Estado por el cual cambiara el expediente una vez se inicie el proceso de secundaria.',
        options: obj.defaultStatusExpediente,
        value: obj.estado_expediente_inicia ? obj.estado_expediente_inicia.id : null,
        order: 5,
        validations: {
          validators: [Validators.required]
        },
      }),
    ];
    return controls.concat(this.getFieldsBase(obj, 'proceso secundario')).sort((a, b) => a.order - b.order);
  }

  /**
   * Campos comunes en los formularios.
   *
   * @private
   * @param {*} obj
   * @param {('categoria' | 'tramite' | 'actividad' | 'tarea' | string)} [type]
   * @returns {BaseControl<any>[]}
   * @memberof CptFormService
   */
  private getFieldsBase(
    obj: any,
    type?: 'categoria' | 'tramite' | 'actividad' | 'tarea' | 'proceso' | 'proceso secundario' | string
  ): BaseControl<any>[] {
    let typePlaceholder = '';
    if (type) {
      switch (type) {
        case 'categoria':
        case 'actividad':
        case 'tarea':
          typePlaceholder = 'de la ' + type;
          break;
        case 'tramite':
        case 'proceso':
        case 'proceso secundario':
          typePlaceholder = 'de el ' + type;
          break;
        default:
          typePlaceholder = type;
          break;
      }
    }
    const controls: BaseControl<any>[] = [
      new Textbox({
        key: 'nombre',
        type: 'text',
        label: 'Nombre:',
        placeholder: 'Ingrese el nombre ' + typePlaceholder,
        value: !isEmpty(obj.nombre) ? obj.nombre : null,
        order: 1,
        validations: {
          validators: [Validators.required, Validators.maxLength(255)]
        }
      }),

      new TextArea({
        key: 'descripcion',
        label: 'Descripción:',
        placeholder: 'Descripción ' + typePlaceholder,
        rows: '2',
        value: !isEmpty(obj.descripcion) ? obj.descripcion : null,
        order: 2,
        validations: {
          validators: [Validators.required, Validators.minLength(5)]
        }
      }),
      new Textbox({
        key: 'anulacion',
        type: 'checkbox',
        label: 'Anulación',
        order: 4,
        class: {
          container: ['form-check', 'form-check-inline']
        },
        value: obj.hasOwnProperty('anulacion') ? obj.anulacion : false,
      })
    ];
    return controls;
  }

  /**
   * Retorna en un array de posiciones el valor de la clave en un array de objetos
   *
   * @private
   * @param {Array<Object>} list
   * @param {string} key
   * @returns {Array<any>}
   * @memberof CptFormService
   */
  private getOptions(list: Array<Object>, key: string): Array<any> {
    const resultList: Array<any> = [];
    if (list && list.length) {
      list.forEach(el => {
        if (el[key]) {
          resultList.push(el[key]);
        }
      });
    }
    return resultList;
  }

}



// WEBPACK FOOTER //
// ./src/app/administration/process-automation/shared/cpt-form.service.ts